// ============================================================================
// PURCHASE Types
// ============================================================================

export interface IPurchase {
  purchase_id: string;
  date: string;
  supplier: string;
  item: string;
  quantity: number;
  unit_price: number;
  total: number;
  status: 'pending' | 'received' | 'cancelled';
  notes: string;
}

export interface IPurchaseListResponse {
  purchases: IPurchase[];
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

export interface IPurchaseCreateRequest {
  date: string;
  supplier: string;
  item: string;
  quantity: number;
  unit_price: number;
  status?: string;
  notes?: string;
}

export interface IPurchaseUpdateRequest {
  date?: string;
  supplier?: string;
  item?: string;
  quantity?: number;
  unit_price?: number;
  status?: string;
  notes?: string;
}

// ============================================================================
// SALES Types
// ============================================================================

export interface ISale {
  sale_id: string;
  date: string;
  customer: string;
  item: string;
  quantity: number;
  unit_price: number;
  total: number;
  status: 'pending' | 'completed' | 'cancelled';
  notes: string;
}

export interface ISaleListResponse {
  sales: ISale[];
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

export interface ISaleCreateRequest {
  date: string;
  customer: string;
  item: string;
  quantity: number;
  unit_price: number;
  status?: string;
  notes?: string;
}

export interface ISaleUpdateRequest {
  date?: string;
  customer?: string;
  item?: string;
  quantity?: number;
  unit_price?: number;
  status?: string;
  notes?: string;
}

// ============================================================================
// INVENTORY Types
// ============================================================================

export interface IInventoryItem {
  id: string;
  month: string;              // A: 月份
  movement_type: string;      // B: 異動類型 (進貨/出貨/盤點/調整)
  main_category: string;      // C: 主類別
  sub_category: string;       // D: 子類別
  date: string;               // E: 日期
  invoice_number: string;     // F: 發票號碼
  invoice_date: string;       // G: 發票日期
  warehouse: string;          // H: 倉庫別
  document_type: string;      // I: 單別 (採購單/銷售單/盤點單/調整單)
  document_number: string;    // J: 單號
  part_code: string;          // K: 料號
  specification: string;      // L: 規格
  quantity: number;           // M: 數量
  unit_price: number;         // N: 單價
  currency: string;           // O: 幣別
}

export interface IInventoryListResponse {
  items: IInventoryItem[];
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

export interface IInventoryCreateRequest {
  month: string;              // A: 月份
  movement_type?: string;     // B: 異動類型 (進貨/出貨/盤點/調整)
  main_category?: string;     // C: 主類別
  sub_category?: string;      // D: 子類別
  date: string;               // E: 日期
  invoice_number?: string;    // F: 發票號碼
  invoice_date?: string;      // G: 發票日期
  warehouse: string;          // H: 倉庫別
  document_type?: string;     // I: 單別 (採購單/銷售單/盤點單/調整單)
  document_number?: string;   // J: 單號
  part_code: string;          // K: 料號
  specification?: string;     // L: 規格
  quantity: number;           // M: 數量
  unit_price: number;         // N: 單價
  currency: string;           // O: 幣別
}

export interface IInventoryUpdateRequest {
  month?: string;
  movement_type?: string;
  main_category?: string;
  sub_category?: string;
  date?: string;
  invoice_number?: string;
  invoice_date?: string;
  warehouse?: string;
  document_type?: string;
  document_number?: string;
  part_code?: string;
  specification?: string;
  quantity?: number;
  unit_price?: number;
  currency?: string;
}

// ============================================================================
// STOCK TAKE Types
// ============================================================================

export interface IStockTakeRecord {
  id: string;
  part_code: string;
  specification: string;
  warehouse: string;
  system_qty: number;
  physical_qty: number;
  variance: number;
  variance_percent: number;
  counted_by: string;
  counted_at: string;
  notes: string;
}

export interface IStockTakeListResponse {
  items: IStockTakeRecord[];
  year: number;
  month: number;
  total_count: number;
}

export interface IStockTakeGenerateRequest {
  year: number;
  month: number;
}

export interface IStockTakeUpdateRequest {
  physical_qty: number;
  counted_by: string;
  notes: string;
}

export interface IStockTakeBatchUpdateRequest {
  updates: {
    id: string;
    physical_qty: number;
    counted_by: string;
    notes: string;
  }[];
}

export interface IStockTakeSession {
  year: number;
  month: number;
  sheet_name: string;
  record_count: number;
  completed_count: number;
  generated_at: string;
}

// ============================================================================
// INVOICE Types
// ============================================================================

export interface IInvoice {
  invoice_no: string;
  date: string;
  customer: string;
  items: string;
  subtotal: number;
  tax: number;
  total: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  due_date: string;
  notes: string;
}

export interface IInvoiceListResponse {
  invoices: IInvoice[];
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

export interface IInvoiceCreateRequest {
  invoice_no?: string;
  date: string;
  customer: string;
  items: string;
  subtotal: number;
  tax?: number;
  status?: string;
  due_date?: string;
  notes?: string;
}

export interface IInvoiceUpdateRequest {
  date?: string;
  customer?: string;
  items?: string;
  subtotal?: number;
  tax?: number;
  status?: string;
  due_date?: string;
  notes?: string;
}

// ============================================================================
// VENDOR Types (Suppliers sheet with 16 columns)
// Columns: 供應商代號, 供應商名稱, 統一編號, 電話, 傳真, 聯絡人, 廠商地址, email, 手機, 銀行名稱, 分行, 銀行代號, 帳戶名稱, 帳號, 幣別, 供應商資料
// ============================================================================

export interface IVendor {
  row_index: number;
  vendor_code: string;      // A - 供應商代號
  vendor_name: string;      // B - 供應商名稱
  tax_id: string;           // C - 統一編號
  phone: string;            // D - 電話
  fax: string;              // E - 傳真
  contact_person: string;   // F - 聯絡人
  address: string;          // G - 廠商地址
  email: string;            // H - email
  mobile: string;           // I - 手機
  bank_name: string;        // J - 銀行名稱
  branch: string;           // K - 分行
  bank_code: string;        // L - 銀行代號
  account_name: string;     // M - 帳戶名稱
  account_number: string;   // N - 帳號
  currency: string;         // O - 幣別
  vendor_info: string;      // P - 供應商資料
}

export interface IVendorCreateRequest {
  vendor_code?: string;
  vendor_name: string;
  tax_id?: string;
  phone?: string;
  fax?: string;
  contact_person?: string;
  address?: string;
  email?: string;
  mobile?: string;
  bank_name?: string;
  branch?: string;
  bank_code?: string;
  account_name?: string;
  account_number?: string;
  currency?: string;
  vendor_info?: string;
}

// ============================================================================
// CUSTOMER Types (國內客戶 sheet with 28 columns)
// Columns: 編號, 級別, 中文名稱, 統一編號, 產品別, 主要產品, 產量, 電話, 傳真, 網站, 出貨條件, 交易幣別, 付款方式, 付款條件, 付款銀行, 付款帳號, 公司地址, 工廠地址, 窗口1, 分機, 手機, Email, 窗口2, 分機, 手機, Email, 負責業務, 往來狀態
// ============================================================================

export interface ICustomer {
  row_index: number;
  customer_id: string;      // A - 編號
  level: string;            // B - 級別
  chinese_name: string;     // C - 中文名稱
  tax_id: string;           // D - 統一編號
  product_type: string;     // E - 產品別
  main_product: string;     // F - 主要產品
  production_vol: string;   // G - 產量
  phone: string;            // H - 電話
  fax: string;              // I - 傳真
  website: string;          // J - 網站
  shipping_terms: string;   // K - 出貨條件
  currency: string;         // L - 交易幣別
  payment_method: string;   // M - 付款方式
  payment_terms: string;    // N - 付款條件
  payment_bank: string;     // O - 付款銀行
  payment_account: string;  // P - 付款帳號
  company_address: string;  // Q - 公司地址
  factory_address: string;  // R - 工廠地址
  contact1: string;         // S - 窗口1
  extension1: string;       // T - 分機
  mobile1: string;          // U - 手機
  email1: string;           // V - Email
  contact2: string;         // W - 窗口2
  extension2: string;       // X - 分機
  mobile2: string;          // Y - 手機
  email2: string;           // Z - Email
  account_manager: string;  // AA - 負責業務
  status: string;           // AB - 往來狀態
}

export interface ICustomerCreateRequest {
  customer_id?: string;
  level?: string;
  chinese_name: string;
  tax_id?: string;
  product_type?: string;
  main_product?: string;
  production_vol?: string;
  phone?: string;
  fax?: string;
  website?: string;
  shipping_terms?: string;
  currency?: string;
  payment_method?: string;
  payment_terms?: string;
  payment_bank?: string;
  payment_account?: string;
  company_address?: string;
  factory_address?: string;
  contact1?: string;
  extension1?: string;
  mobile1?: string;
  email1?: string;
  contact2?: string;
  extension2?: string;
  mobile2?: string;
  email2?: string;
  account_manager?: string;
  status?: string;
}

// ============================================================================
// PART CODE Types (12 category sheets with 16 columns each)
// Columns: 雲騰品號, 廠商名稱, 廠牌, 廠商品名, 廠商規格, 類別, 尺寸, H-O, MPQ
// ============================================================================

export interface ICategoryInfo {
  id: string;
  name: string;
  sheet_name: string;
}

export interface IPartCode {
  row_index: number;
  part_number: string;      // A - 雲騰品號
  vendor_name: string;      // B - 廠商名稱
  brand: string;            // C - 廠牌
  vendor_product: string;   // D - 廠商品名
  vendor_spec: string;      // E - 廠商規格
  category: string;         // F - 類別
  dimension: string;        // G - 尺寸
  col_h: string;            // H
  col_i: string;            // I
  col_j: string;            // J
  col_k: string;            // K
  col_l: string;            // L
  col_m: string;            // M
  col_n: string;            // N
  col_o: string;            // O
  mpq: string;              // P - MPQ
}

export interface IPartCodeCreateRequest {
  part_number?: string;
  vendor_name?: string;
  brand?: string;
  vendor_product?: string;
  vendor_spec?: string;
  category?: string;
  dimension?: string;
  col_h?: string;
  col_i?: string;
  col_j?: string;
  col_k?: string;
  col_l?: string;
  col_m?: string;
  col_n?: string;
  col_o?: string;
  mpq?: string;
}

export interface IPartCodeCellUpdateRequest {
  row_index: number;
  column: string;
  value: string;
}

// ============================================================================
// Cell Update Types
// ============================================================================

export interface ICellUpdateRequest {
  row_index: number;
  column: string;
  value: string;
}

export interface IBatchCellUpdateRequest {
  row_index: number;
  updates: Record<string, string>;
}

// ============================================================================
// FINANCE Types (Cash-in and Cash-out transactions)
// ============================================================================

export type FinanceTransactionType = 'cash_in' | 'cash_out';

export interface IFinanceTransaction {
  transaction_id: string;
  type: FinanceTransactionType;
  date: string;
  amount: number;
  category: string;
  description: string;
  reference: string;
  payment_method: string;
  status: 'pending' | 'completed' | 'cancelled';
  created_by: string;
  notes: string;
}

export interface IFinanceListResponse {
  transactions: IFinanceTransaction[];
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  summary: {
    total_cash_in: number;
    total_cash_out: number;
    net_balance: number;
  };
}

export interface IFinanceCreateRequest {
  type: FinanceTransactionType;
  date: string;
  amount: number;
  category: string;
  description: string;
  reference?: string;
  payment_method?: string;
  status?: string;
  notes?: string;
}

export interface IFinanceUpdateRequest {
  date?: string;
  amount?: number;
  category?: string;
  description?: string;
  reference?: string;
  payment_method?: string;
  status?: string;
  notes?: string;
}

// Finance categories
export const CASH_IN_CATEGORIES = [
  '銷售收入',
  '服務收入',
  '投資收入',
  '利息收入',
  '退款',
  '其他收入',
];

export const CASH_OUT_CATEGORIES = [
  '採購支出',
  '薪資支出',
  '租金支出',
  '水電費',
  '辦公用品',
  '交通費',
  '稅金',
  '其他支出',
];

export const PAYMENT_METHODS = [
  '現金',
  '銀行轉帳',
  '信用卡',
  '支票',
  'Line Pay',
  '其他',
];

// ============================================================================
// Common Types
// ============================================================================

export interface IPSIListParams {
  page?: number;
  pageSize?: number;
  q?: string;
}

export type PSITabType = 'purchases' | 'sales' | 'inventory' | 'invoices' | 'vendors' | 'customers' | 'partcodes' | 'finance' | 'expenditure' | 'wip' | 'flows' | 'quotations';

// ============================================================================
// EXPENDITURE Types (Company Regular Expenses)
// ============================================================================

export type ExpenditurePeriod = 'monthly' | 'quarterly' | 'yearly';
export type ExpenditureStatus = 'pending' | 'paid' | 'cancelled';

export interface IExpenditure {
  expenditure_id: string;
  date: string;
  category: string;
  sub_category: string;
  description: string;
  amount: number;
  currency: string;
  payment_method: string;
  vendor: string;
  invoice_number: string;
  status: ExpenditureStatus;
  is_recurring: boolean;
  recurring_frequency: 'none' | 'monthly' | 'quarterly' | 'yearly';
  notes: string;
  created_by: string;
  created_at: string;
}

export interface IExpenditureListResponse {
  expenditures: IExpenditure[];
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  summary: IExpenditureSummary;
}

export interface IExpenditureSummary {
  total_amount: number;
  by_category: Record<string, number>;
  by_status: Record<ExpenditureStatus, number>;
  period_comparison: {
    current: number;
    previous: number;
    change_percent: number;
  };
}

export interface IExpenditureCreateRequest {
  date: string;
  category: string;
  sub_category?: string;
  description: string;
  amount: number;
  currency?: string;
  payment_method?: string;
  vendor?: string;
  invoice_number?: string;
  status?: ExpenditureStatus;
  is_recurring?: boolean;
  recurring_frequency?: 'none' | 'monthly' | 'quarterly' | 'yearly';
  notes?: string;
}

export interface IExpenditureUpdateRequest {
  date?: string;
  category?: string;
  sub_category?: string;
  description?: string;
  amount?: number;
  currency?: string;
  payment_method?: string;
  vendor?: string;
  invoice_number?: string;
  status?: ExpenditureStatus;
  is_recurring?: boolean;
  recurring_frequency?: 'none' | 'monthly' | 'quarterly' | 'yearly';
  notes?: string;
}

// Expenditure period summary for charts
export interface IExpenditurePeriodSummary {
  period: string;  // e.g., "2024-01" for monthly, "2024-Q1" for quarterly, "2024" for yearly
  total_amount: number;
  by_category: Record<string, number>;
  transaction_count: number;
}

export interface IExpenditurePeriodResponse {
  period_type: ExpenditurePeriod;
  summaries: IExpenditurePeriodSummary[];
  grand_total: number;
  category_totals: Record<string, number>;
}

// Expenditure categories - Company regular expenses
export const EXPENDITURE_CATEGORIES: Record<string, string[]> = {
  '人事費用': ['薪資', '勞保', '健保', '退休金', '獎金', '加班費', '其他人事'],
  '營運費用': ['租金', '水費', '電費', '網路電話', '保險費', '維修費', '清潔費', '其他營運'],
  '辦公費用': ['辦公用品', '設備採購', '軟體訂閱', '雲端服務', '網域服務', '郵電費', '印刷費', '其他辦公'],
  '交通費用': ['油資', '停車費', '過路費', '大眾運輸', '計程車', '差旅費', '其他交通'],
  '物流費用': ['進貨運費', '銷貨運費', '快遞費', '報關費', '倉儲費', '其他物流'],
  '行銷費用': ['廣告費', '展覽費', '公關費', '樣品費', '贈品費', '其他行銷'],
  '稅務規費': ['營業稅', '營所稅', '房屋稅', '牌照稅', '規費', '其他稅務'],
  '財務費用': ['銀行手續費', '利息支出', '匯兌損失', '其他財務'],
  '其他費用': ['雜支', '捐贈', '罰款', '其他'],
};

export const EXPENDITURE_MAIN_CATEGORIES = Object.keys(EXPENDITURE_CATEGORIES);

export const getAllExpenditureSubCategories = (): string[] => {
  return Object.values(EXPENDITURE_CATEGORIES).flat();
};

// ============================================================================
// WORK-IN-PROGRESS (WIP) Types
// ============================================================================

export type WIPDepartment = 'purchase' | 'sales' | 'finance' | 'manufacturing';

export interface IWIPColumn {
  id: string;
  title: string;
  titleZh: string;
}

export interface IWIPTask {
  id: string;
  title: string;
  description?: string;
  department: WIPDepartment;
  column: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignee?: string;
  dueDate?: string;
  reference?: string;
  amount?: number;
  createdAt: string;
  updatedAt: string;
}

export const WIP_COLUMNS: Record<WIPDepartment, IWIPColumn[]> = {
  purchase: [
    { id: 'rfq', title: 'RFQ', titleZh: '詢價單' },
    { id: 'po', title: 'PO', titleZh: '採購單' },
    { id: 'restock', title: 'Restock', titleZh: '補貨' },
    { id: 'payable', title: 'Payable', titleZh: '應付款' },
  ],
  sales: [
    { id: 'quotation', title: 'Quotation', titleZh: '報價單' },
    { id: 'so', title: 'SO', titleZh: '銷售單' },
    { id: 'shipment', title: 'Shipment', titleZh: '出貨' },
    { id: 'dispatch', title: 'Dispatch', titleZh: '派送' },
    { id: 'receivable', title: 'Receivable', titleZh: '應收款' },
  ],
  finance: [
    { id: 'payable', title: 'Payable', titleZh: '應付款' },
    { id: 'receivable', title: 'Receivable', titleZh: '應收款' },
    { id: 'salary', title: 'Salary', titleZh: '薪資' },
    { id: 'cash_ledger', title: 'Cash Ledger', titleZh: '現金帳' },
  ],
  manufacturing: [
    { id: 'mo', title: 'MO', titleZh: '製令單' },
    { id: 'scheduling', title: 'Scheduling', titleZh: '排程' },
    { id: 'return_mfg_good', title: 'Return Mfg Good', titleZh: '成品退回' },
    { id: 'return_material', title: 'Return Material', titleZh: '材料退回' },
    { id: 'refill_material', title: 'Refill Material', titleZh: '補料' },
  ],
};

export const WIP_DEPARTMENT_LABELS: Record<WIPDepartment, { en: string; zh: string }> = {
  purchase: { en: 'Purchase', zh: '採購' },
  sales: { en: 'Sales', zh: '銷售' },
  finance: { en: 'Finance', zh: '財務' },
  manufacturing: { en: 'Manufacturing', zh: '製造' },
};

export const WIP_PRIORITY_COLORS: Record<IWIPTask['priority'], string> = {
  low: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  medium: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300',
  high: 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300',
  urgent: 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300',
};
